import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';
import {
  Sparkle, CheckCircle, ArrowRight, Calendar, Compass, ChatTeardropDots,
  House, Heart, Gauge, IdentificationCard, Coins, Path, ClockClockwise, Robot
} from '@phosphor-icons/react';

const CALENDLY_URL = 'https://calendly.com/sbusbygfi/outsource-solution-strategies';

const STEP_ICONS = {
  '/household': House,
  '/wealth-health': Heart,
  '/retirement-gap': Gauge,
  '/social-security': IdentificationCard,
  '/roth-conversion': Coins,
  '/scenario-planner': Path,
  '/timeline': ClockClockwise,
};

const TOPIC_TILES = [
  { title: 'How the Wealth Health Score™ works', query: 'wealth health score 5 dimensions', section: 'Wealth Health Score™' },
  { title: 'How the Retirement Gap Analyzer works', query: 'retirement gap analyzer closure scenarios', section: 'Retirement Gap Analyzer' },
  { title: 'How Roth Conversion + Social Security combine', query: 'how roth and social security work together', section: 'Roth + Social Security' },
  { title: 'How tools work together', query: 'how do the planning tools work together', section: 'Planning Workflow' },
  { title: 'How to send beta feedback', query: 'how to send feedback', section: 'Feedback Widget' },
  { title: 'How to book a consultation', query: 'how to book a consultation', section: 'Book Consultation' },
];

const GettingStarted = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ steps: [], completed: false });
  const [chunks, setChunks] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [topicContent, setTopicContent] = useState(null);

  useEffect(() => {
    track('getting_started_viewed');
    Promise.all([
      api.get('/onboarding/status'),
      api.get('/user-guide'),
    ]).then(([s, g]) => {
      setData(s.data);
      setChunks(g.data.chunks || []);
    }).catch(() => {});
  }, []);

  const replayOnboarding = async () => {
    try { await api.post('/onboarding/dismiss'); } catch (e) {}
    navigate('/');
    window.location.reload();
  };

  const openTopic = async (tile) => {
    setActiveTopic(tile.section);
    setTopicContent(null);
    try {
      const res = await api.get(`/user-guide/search?q=${encodeURIComponent(tile.query)}&limit=1`);
      setTopicContent(res.data.results?.[0]?.content || null);
    } catch (e) {
      setTopicContent(null);
    }
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Welcome.</strong> PLANIVANCE AI is an educational planning platform. Follow the 8-step journey below to validate your plan, then book a consultation if you'd like personalized guidance.
          </p>
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Getting Started
          </h1>
          <p className="text-base text-[#4A5568]">
            How to use PLANIVANCE AI in 8 deliberate steps.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={replayOnboarding}
            data-testid="replay-onboarding-button"
            className="border-[#0A1128] text-[#0A1128]"
          >
            <Compass size={16} className="mr-2" />
            Replay Welcome Tour
          </Button>
          <Button
            asChild
            data-testid="book-consultation-button"
            className="bg-[#C5A059] hover:bg-[#B08F4A] text-[#0A1128]"
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('consultation_clicked', { source: 'getting_started' })}
            >
              <Calendar size={16} className="mr-2" />
              Book a Consultation
            </a>
          </Button>
        </div>
      </div>

      {/* Step Journey */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-2">
          The recommended user journey
        </h2>
        <p className="text-sm text-[#4A5568] mb-6">
          Each step builds on the previous one. Skip any time, return any time. Step 2 (Wealth Health Score™) is your <strong>financial assessment experience</strong>.
        </p>
        <div className="space-y-3" data-testid="getting-started-steps">
          {data.steps.map((s, idx) => {
            const Icon = s.external ? Calendar : (STEP_ICONS[s.path] || Sparkle);
            return (
              <div
                key={s.id}
                className="flex items-start gap-4 p-4 rounded-md border border-slate-200 hover:border-[#C5A059] transition-all"
                data-testid={`getting-started-step-${s.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#0A1128] text-[#C5A059] flex items-center justify-center flex-shrink-0 cabinet-grotesk font-medium">
                  {s.id}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={18} weight="duotone" className="text-[#C5A059]" />
                    <h3 className="cabinet-grotesk text-base font-medium text-[#0A1128]">{s.title}</h3>
                  </div>
                  <p className="text-sm text-[#4A5568] leading-relaxed">{s.description}</p>
                  {!s.external && (
                    <p className="text-xs text-[#A0AABF] mt-1 ibm-plex">
                      Menu path: Dashboard <ArrowRight size={10} className="inline" /> {s.title}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => s.external ? window.open(s.path, '_blank', 'noopener,noreferrer') : navigate(s.path)}
                  data-testid={`getting-started-open-${s.id}`}
                  className="border-[#0A1128] text-[#0A1128] flex-shrink-0"
                >
                  Open <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Help Topics */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">
          Quick help topics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TOPIC_TILES.map((tile) => (
            <button
              key={tile.title}
              onClick={() => openTopic(tile)}
              data-testid={`topic-tile-${tile.section.replace(/\W+/g, '-').toLowerCase()}`}
              className={`text-left p-4 rounded-md border transition-all ${
                activeTopic === tile.section
                  ? 'bg-[#0A1128] text-white border-[#0A1128]'
                  : 'bg-white border-slate-200 hover:border-[#C5A059]'
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-[0.08em] mb-1 ${
                activeTopic === tile.section ? 'text-[#C5A059]' : 'text-[#C5A059]'
              }`}>{tile.section}</p>
              <p className={`text-sm font-medium ${
                activeTopic === tile.section ? 'text-white' : 'text-[#0A1128]'
              }`}>{tile.title}</p>
            </button>
          ))}
        </div>
        {activeTopic && (
          <div className="mt-4 p-4 bg-slate-50 rounded-md border border-slate-200" data-testid="active-topic-content">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#C5A059] mb-2">{activeTopic}</p>
            <p className="text-sm text-[#1A202C] leading-relaxed whitespace-pre-line">
              {topicContent || 'Loading…'}
            </p>
          </div>
        )}
      </div>

      {/* AI Coach + Feedback CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/ai-coach')}
          data-testid="getting-started-open-ai-coach"
          className="text-left p-5 rounded-md border border-slate-200 bg-white hover:border-[#C5A059] transition-all dashboard-card"
        >
          <Robot size={28} weight="duotone" className="text-[#C5A059] mb-3" />
          <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">Ask the AI Coach</h3>
          <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">
            Get menu-by-menu navigation help and concept explanations. Strictly educational — no personalized advice.
          </p>
        </button>
        <div className="text-left p-5 rounded-md border border-slate-200 bg-white dashboard-card">
          <ChatTeardropDots size={28} weight="duotone" className="text-[#C5A059] mb-3" />
          <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">Send Beta Feedback</h3>
          <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">
            Use the gold "Share Beta Feedback" button in the bottom-left sidebar — it goes directly to the founders.
          </p>
        </div>
      </div>

      {/* Full Reference Library */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">
          Full reference library
        </h2>
        <div className="space-y-3">
          {chunks.map((c) => (
            <details
              key={c.id}
              className="border border-slate-200 rounded-md group"
              data-testid={`guide-chunk-${c.id}`}
            >
              <summary className="cursor-pointer p-3 text-sm font-medium text-[#0A1128] hover:bg-slate-50 group-open:bg-slate-50">
                {c.title}
              </summary>
              <p className="px-3 pb-3 text-sm text-[#4A5568] leading-relaxed whitespace-pre-line">
                {c.content}
              </p>
            </details>
          ))}
        </div>
      </div>

      <p className="text-xs text-[#A0AABF] leading-relaxed">
        <strong>Educational Use Only.</strong> PLANIVANCE AI is not personalized investment, tax, or legal advice. Consult qualified professionals before making consequential decisions. A Product of Outsource Solution Strategies, LLC.
      </p>
    </div>
  );
};

export default GettingStarted;
